import { mediaQueryLarge, startViewTransition } from 'utilities';
import PaginatedList from 'paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#setLayout(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #setLayout = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    const viewTransitionStylesheet = new CSSStyleSheet();
    this.#setViewTransitionName(value, viewTransitionStylesheet);

    await startViewTransition(() => {
      const { classList } = grid;

      for (const className of classList) {
        if (className.startsWith('product-grid-view-')) {
          classList.remove(className);
        }
      }
      classList.add(`product-grid-view-${value}`);
    });

    this.#removeViewTransitionName(viewTransitionStylesheet);

    const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
    sessionStorage.setItem(`product-grid-view-${viewport}`, value);
  };

  /**
   * Sets the view transition name.
   *
   * @param {string} value
   * @param {CSSStyleSheet} viewTransitionStylesheet
   */
  #setViewTransitionName(value, viewTransitionStylesheet) {
    const { cards } = this.refs;
    if (!cards) return;

    for (const card of cards) {
      card.style.setProperty('view-transition-name', `product-card-${card.dataset.productId}`);
      const transitionSelector = value === 'default' ? '::view-transition-old' : '::view-transition-new';
      viewTransitionStylesheet.insertRule(`
        ${transitionSelector}(product-card-${card.dataset.productId}) {
          object-fit: none;
          object-position: top;
        }
      `);
    }

    document.adoptedStyleSheets.push(viewTransitionStylesheet);
  }

  /**
   * Removes the view transition name.
   *
   * @param {CSSStyleSheet} viewTransitionStylesheet
   */
  #removeViewTransitionName(viewTransitionStylesheet) {
    const { cards } = this.refs;
    if (!cards) return;

    for (const card of cards) {
      card.style.removeProperty('view-transition-name');
    }

    document.adoptedStyleSheets = document.adoptedStyleSheets.filter(
      (styleSheet) => styleSheet !== viewTransitionStylesheet
    );
  }

  /**
   * Sets the input checked.
   *
   * @param {string} value
   */
  #setInputChecked(value) {
    const { columnOptions } = this.refs;
    if (Array.isArray(columnOptions)) {
      for (const option of columnOptions) {
        if (option instanceof HTMLInputElement && option.value === value) {
          option.checked = true;
        } else if (option instanceof HTMLInputElement) {
          option.checked = false;
        }
      }
    }
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
